import {
    Component,
    ElementRef,
    Input,
    Output,
    EventEmitter,
    OnInit,
    ViewChild,
    computed,
    signal,
    AfterViewInit,
    OnChanges,
    OnDestroy,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { PageEvent } from '@angular/material/paginator';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { TranslateService } from '@ngx-translate/core';
import { EventsPaginatedService } from '@app/shared/services/events-paginated.service';
import { Subject, takeUntil } from 'rxjs';
import {
    searchState,
    updateSearchText,
    updateSelectedFilterKey,
    resetSearch,
    FilterKeys,
    updatePagination,
    getCurrentViewType,
    updateCurrentViewType,
} from '@app/stores/searchStore';
import { getShowClosed, setShowClosed } from '@app/stores/appStore';
import {
    ViewType,
    ViewToggleConfig,
    ViewHeaderAction,
} from '@app/shared/models/view-config.interface';
import { ViewConfigurationService } from '@app/shared/services/view-config.service';
import { SelectableItem } from '@app/stores/selectionStore';

@Component({
    selector: 'app-paginated-table-filter',
    templateUrl: './paginated-table-filter.component.html',
    styleUrls: ['./paginated-table-filter.component.scss'],
    standalone: false,
})
export class PaginatedTableFilterComponent implements OnInit, OnDestroy {
    @ViewChild('filterSearch') filterSearch: ElementRef<HTMLInputElement>;
    @ViewChild(MatMenuTrigger) public menuTrigger: MatMenuTrigger;

    @Input() keysForFilteringObj: FilterKeys[] = [];
    @Input() showClosedLabel: string = 'OUTGOING-GOODS.SHOW-CLOSED-NOTES';
    @Input() totalItems: number = 0;
    @Input() userRole: 'consignor' | 'consignee' | 'carrier' | 'admin' =
        'consignor';
    @Input() shouldShowLocationFilter: boolean = false;
    @Input() shouldShowColumnFilter: boolean = false;
    @Input() filteredLocationsCount: number = 0;
    @Input() isLocationFilterActive: boolean = false;
    @Input() headerActions: ViewHeaderAction[] = [];
    @Input() currentSelection: SelectableItem[] = [];

    @Output() locationFilterClicked = new EventEmitter<void>();
    @Output() columnFilterClicked = new EventEmitter<void>();
    @Output() headerActionClicked = new EventEmitter<string>();

    // Local component state
    _keysForFilteringObj: FilterKeys[] = [];
    isStatus: boolean = false;
    isDiscrepancies: boolean = false;
    isPalletTickets: boolean = false;
    viewType = 'folder';

    // Notifications state
    hasNewNotifications: boolean = false;
    private destroy$ = new Subject<void>();

    // Signals for reactive state
    searchState = searchState;
    currentViewType = getCurrentViewType;

    // Computed values from search state
    searchText = computed(() => this.searchState().searchText);
    selectedFilterKey = computed(() => this.searchState().selectedFilterKey);
    showClosed = computed(() => getShowClosed());
    loading = computed(() => this.searchState().loading);

    // Computed values for view switching
    viewToggleConfigComputed = computed(() => {
        return this.viewConfigService.getViewToggleConfig(this.userRole);
    });

    availableViews = computed(() => {
        const config = this.viewToggleConfigComputed();
        return (config.views || []).filter(view => {
            if (view.visible === false) return false;
            if (typeof view.visible === 'function') {
                return view.visible(); // Call the function to determine visibility
            }
            return view.visible == null || view.visible === true; // Default to true if not specified
        });
    });

    shouldShowViewToggle = computed(() => {
        return this.availableViews().length > 1;
    });
    pagination = computed(() => this.searchState().pagination);

    // Computed pagination values
    currentPage = computed(
        () => Math.floor(this.pagination().offset / this.pagination().limit) + 1
    );
    totalPages = computed(() =>
        Math.ceil(this.totalItems / this.pagination().limit)
    );

    statuses: FilterKeys[] = [
        {
            keyName: 'open',
            label: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.OPEN',
        },
        {
            keyName: 'sent',
            label: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.SENT',
        },
        {
            keyName: 'closed',
            label: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.CLOSED',
        },
    ];

    selectedStatus: string = '';
    selectedDiscrepancies: string = '';
    selectedPalletTickets: string = '';

    boolStatus: FilterKeys[] = [
        {
            keyName: 'true',
            label: 'TRUE',
        },
        {
            keyName: 'false',
            label: 'FALSE',
        },
    ];

    constructor(
        private translate: TranslateService,
        private sessionService: SessionService,
        private viewConfigService: ViewConfigurationService,
        private eventsPaginatedService: EventsPaginatedService
    ) {}

    ngOnInit(): void {
        this._keysForFilteringObj = this.keysForFilteringObj;

        // Subscribe to new notifications for the bell icon
        this.eventsPaginatedService.newNotifications$
            .pipe(takeUntil(this.destroy$))
            .subscribe(hasNew => {
                this.hasNewNotifications = hasNew;
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onFilterChanges(filterKey: FilterKeys): void {
        updateSelectedFilterKey(filterKey);
        updateSearchText('');
        this.closeFilterMenu();
        this._keysForFilteringObj = [filterKey];
        this.isStatus = false;
        this.selectedStatus = '';
    }

    onStatusFilterChanges(filterKey: FilterKeys): void {
        let statusKey: FilterKeys;

        if (
            this.keysForFilteringObj.some(
                key => key.keyName === 'deliveryNoteBundleStatus'
            )
        ) {
            statusKey = {
                keyName: 'deliveryNoteBundleStatus',
                label: 'STATUS.STATE',
            };
        } else {
            statusKey = { keyName: 'status', label: 'STATUS.STATE' };
        }

        this._keysForFilteringObj = [statusKey];
        updateSelectedFilterKey(statusKey);
        updateSearchText(filterKey.keyName); // Store actual status value (open/closed/sent)
        this.isStatus = true;
        this.isDiscrepancies = false;
        this.isPalletTickets = false;
        this.selectedStatus = filterKey.keyName;
    }

    onPalletFilterChanges(filterKey: FilterKeys): void {
        const palletKey: FilterKeys = {
            keyName: 'palletTickets',
            label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.PALLET-TICKET',
        };
        this.isDiscrepancies = false;
        this.isStatus = false;
        this.isPalletTickets = true;
        this._keysForFilteringObj = [palletKey];
        updateSelectedFilterKey(palletKey);
        updateSearchText(
            this.translate.instant(filterKey.keyName.toUpperCase())
        );
    }

    onDiscrepanciesFilterChanges(filterKey: FilterKeys): void {
        const discrepanciesKey: FilterKeys = {
            keyName: 'discrepancies',
            label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DISCREPANCIES',
        };

        this._keysForFilteringObj = [discrepanciesKey];
        this.isDiscrepancies = true;
        this.isStatus = false;
        this.isPalletTickets = false;
        updateSelectedFilterKey(discrepanciesKey);
        updateSearchText(
            this.translate.instant(filterKey.keyName.toUpperCase())
        );
    }

    onSearchTextChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        const text = input.value;
        this.updateSearchText(text);
    }

    private updateSearchText(text: string): void {
        // Filter out session email address to prevent accidental search
        const userSession = this.sessionService.getSession();
        if (text === userSession?.emailAddress) {
            this.resetFilter();
            return;
        }

        updateSearchText(text);
    }

    onShowClosedChange(showClosed: boolean): void {
        setShowClosed(showClosed);

        // Trigger data refresh to reload with new filter
        const { triggerDataRefresh } = require('@app/stores/searchStore');
        triggerDataRefresh();
    }

    resetFilter(): void {
        updateSearchText('');
        updateSelectedFilterKey(null);
        this._keysForFilteringObj = this.keysForFilteringObj;
        this.isStatus = false;
        this.isPalletTickets = false;
        this.isDiscrepancies = false;
        this.selectedStatus = '';
        this.selectedDiscrepancies = '';
        this.selectedPalletTickets = '';
        this.closeFilterMenu();
    }

    closeFilterMenu(): void {
        this.menuTrigger?.closeMenu();
    }

    clearSearchInput(): void {
        if (!this.isStatus && !this.isDiscrepancies && !this.isPalletTickets) {
            if (this.filterSearch?.nativeElement) {
                this.filterSearch.nativeElement.value = '';
            }
            this.updateSearchText('');
        }
    }

    getDisplayValue(): string {
        const searchText = this.searchText();

        // If filtering by status, show translated text
        if (this.isStatus && searchText) {
            const statusTranslationMap: { [key: string]: string } = {
                open: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.OPEN',
                sent: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.SENT',
                closed: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.CLOSED',
            };

            const translationKey = statusTranslationMap[searchText];
            if (translationKey) {
                return this.translate.instant(translationKey);
            }
        }

        // For all other cases, return the raw search text
        return searchText;
    }

    // View type switching
    onViewTypeChange(event: MatButtonToggleChange): void {
        const newViewType = event.value as ViewType;

        // Get the search config for the new view type
        const viewConfig = this.viewConfigService.getViewConfig(
            this.userRole,
            newViewType
        );
        const searchConfig = viewConfig?.searchConfig;
        this._keysForFilteringObj = [];

        // Clear notifications when switching to notifications view
        if (newViewType === 'notifications') {
            this.eventsPaginatedService.setNewNotificationsToFalse();
        }

        // update show-close label
        switch (newViewType) {
            case 'folders':
                this.showClosedLabel = 'OUTGOING-GOODS.SHOW-CLOSED';
                break;
            case 'deliverynotes':
                this.showClosedLabel = 'OUTGOING-GOODS.SHOW-CLOSED-NOTES';
                break;
            case 'tours':
                this.showClosedLabel = 'SPEDITEUR.TABLE.SHOW-CLOSED';
                break;
        }

        // reset all boolean filters
        this.isStatus = false;
        this.selectedStatus = '';

        this.isPalletTickets = false;
        this.selectedPalletTickets = '';

        this.isDiscrepancies = false;
        this.selectedDiscrepancies = '';

        updateCurrentViewType(newViewType, searchConfig);
    }

    // Pagination methods
    onPageChange(event: PageEvent): void {
        const offset = event.pageIndex * event.pageSize;
        updatePagination({ limit: event.pageSize, offset });
    }

    // Location filter methods
    onLocationFilterClick(): void {
        this.locationFilterClicked.emit();
    }

    isFilteringLocations(): boolean {
        return this.isLocationFilterActive;
    }

    getFilteredLocationsCount(): number {
        return this.filteredLocationsCount;
    }

    // Column filter methods
    onColumnFilterClick(): void {
        this.columnFilterClicked.emit();
    }

    // Action button methods
    onHeaderActionClick(actionId: string): void {
        this.headerActionClicked.emit(actionId);
    }

    isHeaderActionVisible(actionId: string): boolean {
        const action = this.headerActions.find(a => a.id === actionId);
        if (!action) return false;
        return action.visible
            ? action.visible(undefined, this.currentSelection)
            : true;
    }

    isHeaderActionDisabled(actionId: string): boolean {
        const action = this.headerActions.find(a => a.id === actionId);
        if (!action) return true;
        return action.disabled
            ? action.disabled(undefined, this.currentSelection)
            : false;
    }
}
